import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { DocusealForm } from "@docuseal/react";
import {
  Upload, FileText, CheckCircle, Clock, XCircle, ChevronRight,
  Building2, User, Mail, Phone, Shield, AlertCircle, Loader2,
  ExternalLink, RefreshCw, ClipboardList, ChevronDown
} from "lucide-react";

// ─── Share360 Business portal step order (single source of truth) ────────────
// Required order: 1) Processing Questionnaire, 2) Processing Statements,
// 3) Merchant Information Form, 4) Government ID, 5) Current Bank Statement.
// Moved to shared/consultationEligibility.ts so the server-side consultation
// eligibility gate uses the EXACT same completeness rules as this checklist -
// re-exported here so existing imports/call sites in this file are unchanged.
//
// Note: each step's actionable card, once unlocked, stays visible even after
// later steps are also unlocked/completed (the card components self-manage a
// collapsed "already submitted" view) — so these are strictly "unlocked from
// this point on" gates, not a single mutually-exclusive "active step" state.
export {
  type Share360BusinessProgress,
  isShare360Step2Unlocked,
  isShare360Step3Unlocked,
  isShare360Steps4And5Unlocked,
  getShare360Step4Or5DocType,
  isShare360BusinessComplete,
} from "@shared/consultationEligibility";
import {
  isShare360Step2Unlocked,
  isShare360Step3Unlocked,
  isShare360Steps4And5Unlocked,
  getShare360Step4Or5DocType,
  isShare360BusinessComplete,
} from "@shared/consultationEligibility";

// ─── Share360 Processing Questionnaire ────────────────────────────────────────
type CheckboxOption = { value: string; label: string; detailKey?: string; detailLabel?: string };
type S360Question = {
  key: string;
  label: string;
  type: "text" | "select" | "textarea" | "checkbox-group" | "radio";
  options?: string[];
  checkboxOptions?: CheckboxOption[];
  optional?: boolean;
  sectionStart?: string; // renders a section divider + heading before this field
  sectionNote?: string;  // small descriptive note shown under the section heading
};

const S360_BUSINESS_QUESTIONS: S360Question[] = [
  { key: "business_legal_name",  label: "Legal Business / Organization Name",  type: "text" },
  { key: "business_type",        label: "Type of Business / Entity",           type: "select", options: ["Sole Proprietor", "LLC", "S-Corp", "C-Corp", "Nonprofit", "Partnership", "Other"] },
  { key: "years_in_business",    label: "How long have you been in business?", type: "select", options: ["Less than 1 year", "1–2 years", "3–5 years", "6–10 years", "10+ years"] },
  { key: "currently_processing", label: "Do you currently accept card payments?", type: "select", options: ["Yes", "No", "Sometimes"] },
  { key: "current_processor",    label: "Current payment processor (if any)",  type: "text",     optional: true },
  { key: "monthly_card_volume",  label: "Estimated monthly card volume ($)",   type: "text" },
  // ── From Processing Questionnaire PDF ──────────────────────────────────────
  {
    key: "accepting_payments_methods",
    label: "How are you accepting payments now? (Check all that apply)",
    type: "checkbox-group",
    checkboxOptions: [
      { value: "credit_card_terminal",    label: "Credit card terminal",                                 detailKey: "accepting_payments_terminal_type", detailLabel: "Terminal Type" },
      { value: "pos_system",             label: "Point of Sale system (POS)",                           detailKey: "accepting_payments_pos_name",      detailLabel: "POS System Name" },
      { value: "online_customer_enters", label: "Online – customers enter their own payment information" },
      { value: "online_merchant_enters", label: "Online – merchant enters payment information" },
      { value: "phone_merchant_enters",  label: "Phone – merchant enters payment information" },
    ],
  },
  { key: "invoicing_over_50_percent", label: "Invoicing more than 50%?", type: "radio", options: ["Yes", "No"] },
  {
    key: "payment_types_accepted",
    label: "What kinds of payments are you currently accepting? (Check all that apply)",
    type: "checkbox-group",
    checkboxOptions: [
      { value: "credit_debit_card", label: "Credit/Debit card" },
      { value: "ach",               label: "ACH" },
      { value: "gift_card",         label: "Gift Card" },
      { value: "other",             label: "Other", detailKey: "payment_types_other_specify", detailLabel: "Please specify" },
    ],
  },
  {
    key: "software_integrations",
    label: "Are your payments integrated into any software solutions such as QuickBooks?",
    type: "checkbox-group",
    checkboxOptions: [
      { value: "no_integrations", label: "No integrations" },
      { value: "yes_integrated",  label: "Yes", detailKey: "software_integrations_specify", detailLabel: "Please specify (e.g. QuickBooks, Shopify)" },
    ],
  },
  { key: "additional_notes",     label: "Additional notes or questions",       type: "textarea", optional: true },
];

const S360_NONPROFIT_QUESTIONS: S360Question[] = [
  // ── Organization Information ─────────────────────────────────────────────────
  { key: "nonprofit_name",    label: "Official Nonprofit Name",              type: "text", sectionStart: "Organization Information" },
  { key: "contact_person",    label: "Primary Contact Name",                 type: "text" },
  { key: "email_address",     label: "Email Address",                        type: "text" },
  { key: "phone_number",      label: "Phone Number",                         type: "text" },
  { key: "nonprofit_address", label: "Nonprofit Address",                    type: "text" },
  // ── Verification Details ─────────────────────────────────────────────────────
  { key: "ein",               label: "Employer Identification Number (EIN)", type: "text", sectionStart: "Verification Details" },
  { key: "status_501c3",      label: "501(c)(3) Status",                     type: "text" },
  // ── Online Presence ──────────────────────────────────────────────────────────
  { key: "website",           label: "Website",                              type: "text", optional: true, sectionStart: "Online Presence" },
  { key: "facebook",          label: "Facebook",                             type: "text", optional: true },
  { key: "instagram",         label: "Instagram",                            type: "text", optional: true },
  { key: "other_social",      label: "Other Social Media",                   type: "text", optional: true },
  // ── Banking Information (ACH) ────────────────────────────────────────────────
  { key: "bank_name",         label: "Financial Institution (Bank Name)",    type: "text", sectionStart: "Banking Information (ACH)", sectionNote: "ACH information is required to facilitate secure electronic deposits and is handled with strict confidentiality." },
  { key: "routing_number",    label: "Routing Number",                       type: "text" },
  { key: "account_number",    label: "Account Number",                       type: "text" },
];

function Share360Questionnaire({ token, existingAnswers, onSubmitted, questions, title }: {
  token: string;
  existingAnswers?: Record<string, string>;
  onSubmitted: () => void;
  questions: S360Question[];
  title: string;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>(existingAnswers ?? {});
  const [open, setOpen] = useState(!existingAnswers || Object.keys(existingAnswers).length === 0);
  const saveMutation = trpc.clientPortal.saveQuestionnaire.useMutation({
    onSuccess: () => { toast.success("Questionnaire submitted!"); onSubmitted(); setOpen(false); },
    onError: (e: any) => toast.error(e.message ?? "Failed to save. Please try again."),
  });

  const alreadySubmitted = existingAnswers && Object.keys(existingAnswers).length > 0;
  const isComplete = questions.filter(q => !q.optional).every(q => {
    const val = answers[q.key];
    if (q.type === "checkbox-group") {
      try { return JSON.parse(val || "[]").length > 0; } catch { return false; }
    }
    return !!val?.trim();
  });

  const handleSubmit = () => {
    if (!isComplete) { toast.error("Please answer all required fields."); return; }
    saveMutation.mutate({ token, answers });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <button className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-3">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center ${alreadySubmitted ? "bg-[#84ab4a]" : "bg-amber-100"}`}>
            {alreadySubmitted ? <CheckCircle className="w-4 h-4 text-white" /> : <ClipboardList className="w-4 h-4 text-amber-600" />}
          </div>
          <div className="text-left">
            <div className="text-sm font-bold text-gray-800">{title}</div>
            <div className="text-xs text-gray-400">{alreadySubmitted ? "Submitted — click to review" : "Required — please complete"}</div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-gray-100 pt-4">
          <div className="space-y-4">
            {questions.map((q, qi) => (
              <div key={q.key}>
                {q.sectionStart && (
                  <div className={`mb-3 ${qi > 0 ? "pt-3 border-t border-gray-100" : ""}`}>
                    <div className="text-[11px] font-bold text-gray-600 uppercase tracking-wider">{q.sectionStart}</div>
                    {q.sectionNote && <p className="text-xs text-gray-400 mt-1 leading-relaxed">{q.sectionNote}</p>}
                  </div>
                )}
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {q.label}
                  {!q.optional && <span className="text-red-400 ml-1">*</span>}
                </label>
                {q.type === "checkbox-group" && q.checkboxOptions ? (
                  <div className="space-y-2 pt-1">
                    {q.checkboxOptions.map(opt => {
                      const selected: string[] = (() => { try { return JSON.parse(answers[q.key] || "[]"); } catch { return []; } })();
                      const checked = selected.includes(opt.value);
                      const toggle = () => {
                        if (alreadySubmitted) return;
                        const next = checked ? selected.filter(v => v !== opt.value) : [...selected, opt.value];
                        setAnswers(a => ({ ...a, [q.key]: JSON.stringify(next) }));
                      };
                      return (
                        <div key={opt.value}>
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input type="checkbox" checked={checked} onChange={toggle} disabled={alreadySubmitted} className="w-4 h-4 accent-[#84ab4a] flex-shrink-0" />
                            <span className="text-sm text-gray-700">{opt.label}</span>
                          </label>
                          {checked && opt.detailKey && (
                            <input
                              type="text"
                              className="mt-1 ml-6 w-[calc(100%-1.5rem)] border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40"
                              placeholder={opt.detailLabel ?? "Please specify"}
                              value={answers[opt.detailKey] ?? ""}
                              onChange={e => setAnswers(a => ({ ...a, [opt.detailKey!]: e.target.value }))}
                              disabled={alreadySubmitted}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : q.type === "radio" ? (
                  <div className="flex items-center gap-6 pt-1">
                    {q.options!.map(o => (
                      <label key={o} className="flex items-center gap-1.5 cursor-pointer select-none">
                        <input type="radio" name={q.key} value={o} checked={answers[q.key] === o} onChange={() => { if (!alreadySubmitted) setAnswers(a => ({ ...a, [q.key]: o })); }} disabled={alreadySubmitted} className="w-4 h-4 accent-[#84ab4a]" />
                        <span className="text-sm text-gray-700">{o}</span>
                      </label>
                    ))}
                  </div>
                ) : q.type === "select" ? (
                  <select
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40"
                    value={answers[q.key] ?? ""}
                    onChange={e => setAnswers(a => ({ ...a, [q.key]: e.target.value }))}
                    disabled={alreadySubmitted && !open}
                  >
                    <option value="">— Select —</option>
                    {q.options!.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : q.type === "textarea" ? (
                  <textarea
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 resize-none"
                    rows={3}
                    value={answers[q.key] ?? ""}
                    onChange={e => setAnswers(a => ({ ...a, [q.key]: e.target.value }))}
                    placeholder="Optional"
                  />
                ) : (
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40"
                    value={answers[q.key] ?? ""}
                    onChange={e => setAnswers(a => ({ ...a, [q.key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>
          {!alreadySubmitted && (
            <button
              className="mt-5 w-full bg-[#84ab4a] text-white font-semibold rounded-xl py-2.5 text-sm hover:bg-[#6f9139] transition-colors disabled:opacity-50"
              onClick={handleSubmit}
              disabled={saveMutation.isPending || !isComplete}
            >
              {saveMutation.isPending ? "Submitting…" : "Submit Questionnaire"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Stage labels ─────────────────────────────────────────────────────────────
const STAGE_LABELS: Record<string, { label: string; color: string; step: number }> = {
  lead_captured:      { label: "Inquiry Received",    color: "#6B7280", step: 1 },
  audit_submitted:    { label: "Audit Submitted",      color: "#3B82F6", step: 2 },
  follow_up_active:   { label: "In Review",            color: "#F59E0B", step: 3 },
  payment_received:   { label: "Payment Confirmed",    color: "#8B5CF6", step: 4 },
  onboarding_submitted:{ label: "Onboarding",          color: "#EC4899", step: 5 },
  active_client:      { label: "Active Client",        color: "#10B981", step: 6 },
  lost_cold:          { label: "Closed",               color: "#EF4444", step: 0 },
};

const PIPELINE_STEPS = [
  "lead_captured", "audit_submitted", "follow_up_active",
  "payment_received", "onboarding_submitted", "active_client"
];

// ─── Document types clients can upload ────────────────────────────────────────
const DOC_TYPES = [
  { value: "id_front",               label: "Government ID (Front)" },
  { value: "id_back",                label: "Government ID (Back)" },
  { value: "bank_statement",         label: "Processing Statement (Monthly)" },
  { value: "current_bank_statement", label: "Current Bank Statement" },
  { value: "tax_return",             label: "Tax Return" },
  { value: "business_license",       label: "Business License" },
  { value: "voided_check",           label: "Voided Check" },
  { value: "501c3",                  label: "501(c)(3) Determination Letter" },
  { value: "logo",                   label: "Organization Logo" },
  { value: "marketing_materials",    label: "Marketing Materials" },
  { value: "articles",               label: "Articles of Incorporation" },
  { value: "other",                  label: "Other Document" },
];

// ─── Legacy360 Living Trust document types ───────────────────────────────────
const L360_DOC_TYPES = [
  { value: "l360_bank_statements",    label: "Most Recent Banking / Credit Union Statements",    hint: "Upload your most recent statements (1–3 months)." },
  { value: "l360_banking_info",       label: "Banking Accounts Information",                     hint: "Account names, numbers, and institution details." },
  { value: "l360_mortgage_statement", label: "Mortgage Statement",                               hint: "Most recent mortgage statement for each property." },
  { value: "l360_retirement_accounts",label: "Retirement / Brokerage Accounts",                  hint: "401k, IRA, brokerage account statements." },
  { value: "l360_life_insurance",     label: "Life Insurance Policies",                          hint: "Current life insurance policy documents." },
  { value: "l360_annuities",          label: "Annuities",                                        hint: "Annuity policy or contract documents." },
  { value: "l360_property_deed",      label: "Most Recent Property Deed",                        hint: "Deed for each property to be included in the trust." },
  { value: "other",                   label: "Other Document",                                   hint: "Any additional documents relevant to your estate." },
];

// ─── Legacy360 Portal Document Section ───────────────────────────────────────
function Legacy360DocumentSection({ token, documents, onUploaded }: {
  token: string;
  documents: any[];
  onUploaded: () => void;
}) {
  const totalRejected = documents.filter(d => d.status === "rejected").length;
  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
        <p className="text-sm font-semibold text-amber-800 mb-1">Document Upload Instructions</p>
        <p className="text-xs text-amber-700">
          To prepare your Living Trust, please upload the documents below. Each upload is optional — provide what you have available.
          Your estate planning team will review everything and contact you if additional information is needed.
        </p>
      </div>

      {/* Action required banner */}
      {totalRejected > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">
              Action Required — {totalRejected} document{totalRejected > 1 ? "s" : ""} need{totalRejected === 1 ? "s" : ""} resubmission
            </p>
            <p className="text-xs text-red-600 mt-0.5">
              Review the rejection reason on each document below and upload a corrected file.
            </p>
          </div>
        </div>
      )}

      {L360_DOC_TYPES.map(docType => {
        const uploaded = documents.filter(d => d.docType === docType.value);
        const hasRejected = uploaded.some(d => d.status === "rejected");
        return (
          <div key={docType.value} className={`bg-white rounded-2xl shadow-sm overflow-hidden ${hasRejected ? "border border-red-200" : "border border-gray-100"}`}>
            <div className={`px-5 py-4 border-b ${hasRejected ? "border-red-100 bg-red-50/40" : "border-gray-50"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-gray-800">{docType.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{docType.hint}</div>
                </div>
                {uploaded.length > 0 && (
                  hasRejected ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5">
                      <XCircle className="w-3 h-3" /> Action Required
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
                      <CheckCircle className="w-3 h-3" /> {uploaded.length} uploaded
                    </span>
                  )
                )}
              </div>
            </div>
            <div className="px-5 py-3 space-y-3">
              {/* Uploaded files with status — shown above the upload card */}
              {uploaded.length > 0 && (
                <div className="space-y-2">
                  {uploaded.map((doc: any) => (
                    <div key={doc.id} className={`rounded-xl border ${doc.status === "rejected" ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-100"}`}>
                      <div className="flex items-center gap-2 px-3 py-2 text-xs">
                        {doc.status === "rejected"
                          ? <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                          : <FileText className="w-3.5 h-3.5 text-[#84ab4a] flex-shrink-0" />}
                        <span className="truncate flex-1 text-gray-700">{doc.fileName}</span>
                        <DocStatusBadge status={doc.status} />
                        <a href={`/api/files/${doc.fileKey}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-1">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                      {doc.status === "rejected" && (
                        <div className="px-3 pb-2">
                          <div className="rounded-lg bg-red-100 border border-red-200 px-3 py-2">
                            <p className="text-xs font-semibold text-red-700 mb-0.5">Reason for rejection</p>
                            <p className="text-xs text-red-600">
                              {doc.reviewNote ?? "No reason provided — please contact your agent for details."}
                            </p>
                          </div>
                          <p className="text-xs text-red-600 mt-2 font-medium">Please upload a corrected file below.</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <UploadCard
                token={token}
                onUploaded={onUploaded}
                allowedTypes={[docType.value]}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function DocStatusBadge({ status }: { status: string }) {
  if (status === "submitted" || status === "approved") return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
      <CheckCircle className="w-3 h-3" /> Submitted
    </span>
  );
  if (status === "rejected") return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5">
      <XCircle className="w-3 h-3" /> Action Required
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
      <Clock className="w-3 h-3" /> Submitted
    </span>
  );
}

// ─── Upload Card ──────────────────────────────────────────────────────────────
const ALL_DOC_TYPES: { value: string; label: string }[] = [
  ...DOC_TYPES,
  ...L360_DOC_TYPES.map(({ value, label }) => ({ value, label })),
];

function UploadCard({ token, onUploaded, allowedTypes }: { token: string; onUploaded: () => void; allowedTypes?: string[] }) {
  const filteredDocTypes = allowedTypes ? ALL_DOC_TYPES.filter(d => allowedTypes.includes(d.value)) : DOC_TYPES;
  const [docType, setDocType] = useState(filteredDocTypes[0]?.value ?? "id_front");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)"); return; }
    setUploading(true);
    try {
      const docLabel = ALL_DOC_TYPES.find(d => d.value === docType)?.label ?? docType;
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(
        `/api/upload/client-document?token=${encodeURIComponent(token)}&docType=${encodeURIComponent(docType)}&docLabel=${encodeURIComponent(docLabel)}`,
        { method: "POST", body: formData }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Upload failed");
      toast.success("Document uploaded successfully!");
      onUploaded();
    } catch (err: any) {
      toast.error(err.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
        <Upload className="w-4 h-4 text-[#84ab4a]" /> Upload a Document
      </h3>
      {filteredDocTypes.length > 1 && (
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Document Type</label>
          <select
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40"
            value={docType}
            onChange={e => setDocType(e.target.value)}
          >
            {filteredDocTypes.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
      )}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${dragOver ? "border-[#84ab4a] bg-[#84ab4a]/5" : "border-gray-200 hover:border-[#84ab4a]/50 hover:bg-gray-50"}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-[#84ab4a] animate-spin" />
            <p className="text-sm text-gray-500">Uploading…</p>
          </div>
        ) : (
          <>
            <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-600">Drop file here or <span className="text-[#84ab4a] underline">browse</span></p>
            <p className="text-xs text-gray-400 mt-1">JPEG, PNG, PDF · Max 10 MB</p>
          </>
        )}
        <input ref={fileRef} type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf,.webp" onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
      </div>
    </div>
  );
}

// ─── Share360 Merchant Information Form (DocuSeal embedded) ─────────────────
// Uses DocuSeal template 3538142. Clients fill the form in an embedded iframe —
// no DocuSeal account or login required. On completion, saves submission metadata
// to lead.metadata.s360_merchant_info via clientPortal.saveMerchantInfo.

function NonprofitAgreementCard({ token, existingAnswers, agreementDoc, onSubmitted }: {
  token: string;
  existingAnswers?: Record<string, string>;
  agreementDoc?: { status: string; reviewNote?: string | null } | null;
  onSubmitted: () => void;
}) {
  const docStatus = agreementDoc?.status ?? null;
  const isRejected = docStatus === "rejected";
  const isApproved = docStatus === "approved";
  const isPendingReview = docStatus === "submitted" || docStatus === "pending";
  const alreadySubmitted = !!(existingAnswers && Object.keys(existingAnswers).length > 0) && !isRejected;
  const [open, setOpen] = useState(!alreadySubmitted);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [embedSrc, setEmbedSrc] = useState<string | null>(null);
  const [submissionId, setSubmissionId] = useState<number | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const createSession = trpc.clientPortal.createNonprofitAgreementSession.useMutation({
    onSuccess: (data) => { setEmbedSrc(data.embedSrc); setSubmissionId(data.submissionId); },
    onError: (e: any) => setSessionError(e.message ?? "Could not load the form. Please try again."),
  });

  const saveMutation = trpc.clientPortal.saveNonprofitAgreement.useMutation({
    onSuccess: () => { toast.success("Nonprofit Agreement submitted!"); onSubmitted(); setOpen(false); },
    onError: (e: any) => toast.error(e.message ?? "Failed to save. Please try again."),
  });

  useEffect(() => {
    if (!alreadySubmitted && !embedSrc && !createSession.isPending) {
      createSession.mutate({ token });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !embedSrc && !alreadySubmitted && !isResubmitting && !createSession.isPending) {
      createSession.mutate({ token });
    }
  };

  const handleStartResubmit = () => {
    setIsResubmitting(true);
    setOpen(true);
    setEmbedSrc(null);
    setSubmissionId(null);
    createSession.mutate({ token });
  };

  const handleComplete = (data: any) => {
    const docUrl = (data?.documents?.[0]?.url as string | undefined) ?? "";
    const sid = submissionId ?? (data?.submission?.id as number | undefined);
    saveMutation.mutate({
      token,
      answers: {
        completed_via: "docuseal",
        docuseal_submission_id: String(sid ?? ""),
        document_url: docUrl,
        completed_at: new Date().toISOString(),
      },
    });
    setIsResubmitting(false);
  };

  const headerIcon = isRejected
    ? <XCircle className="w-4 h-4 text-white" />
    : isApproved
      ? <CheckCircle className="w-4 h-4 text-white" />
      : isPendingReview
        ? <Clock className="w-4 h-4 text-white" />
        : <ClipboardList className="w-4 h-4 text-amber-600" />;
  const headerIconBg = isRejected ? "bg-red-500" : isApproved ? "bg-[#84ab4a]" : isPendingReview ? "bg-amber-400" : "bg-amber-100";
  const headerSub = isRejected
    ? "Action Required — agreement needs corrections"
    : isApproved
      ? "Approved"
      : isPendingReview
        ? "Pending Review"
        : "Required — complete the agreement below";

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <button className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" onClick={handleToggle}>
        <div className="flex items-center gap-3">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center ${headerIconBg}`}>
            {headerIcon}
          </div>
          <div className="text-left">
            <div className="text-sm font-bold text-gray-800">Step 2 — Nonprofit Agreement</div>
            <div className={`text-xs ${isRejected ? "text-red-500 font-semibold" : "text-gray-400"}`}>{headerSub}</div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-gray-100">
          {isRejected && !isResubmitting ? (
            <div className="px-6 py-6">
              <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm font-bold text-red-700">Agreement Needs Corrections</p>
                </div>
                <p className="text-xs font-semibold text-red-700 mb-1">Reason:</p>
                <p className="text-xs text-red-600">{agreementDoc?.reviewNote ?? "No reason provided — please contact your agent for details."}</p>
              </div>
              <button
                onClick={handleStartResubmit}
                className="w-full py-2.5 rounded-xl bg-[#84ab4a] text-white text-sm font-semibold hover:bg-[#6d9040] transition-colors"
              >
                Correct & Resubmit Agreement
              </button>
            </div>
          ) : isApproved ? (
            <div className="px-6 py-8 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-emerald-700">Nonprofit Agreement approved.</p>
            </div>
          ) : isPendingReview && !isResubmitting ? (
            <div className="px-6 py-8 text-center">
              <Clock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-amber-700">Pending Review</p>
              <p className="text-xs text-gray-400 mt-1">Your agreement has been received and is under review by our team.</p>
            </div>
          ) : createSession.isPending ? (
            <div className="px-6 py-12 text-center">
              <Loader2 className="w-8 h-8 text-[#84ab4a] animate-spin mx-auto" />
              <p className="text-sm text-gray-400 mt-3">Loading agreement…</p>
            </div>
          ) : sessionError ? (
            <div className="px-6 py-8 text-center">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-600 mb-3">{sessionError}</p>
              <button
                onClick={() => { setSessionError(null); createSession.mutate({ token }); }}
                className="text-xs text-[#84ab4a] underline hover:text-[#6f9139]"
              >
                Try again
              </button>
            </div>
          ) : embedSrc ? (
            <DocusealForm
              src={embedSrc}
              withTitle={false}
              withDownloadButton={false}
              autoscrollFields={true}
              onComplete={handleComplete}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function MerchantDocuSealCard({ token, existingAnswers, merchantDoc, onSubmitted }: {
  token: string;
  existingAnswers?: Record<string, string>;
  merchantDoc?: { status: string; reviewNote?: string | null } | null;
  onSubmitted: () => void;
}) {
  const docStatus = merchantDoc?.status ?? null;
  const isRejected = docStatus === "rejected";
  const isApproved = docStatus === "approved";
  const isPendingReview = docStatus === "submitted" || docStatus === "pending";
  const alreadySubmitted = !!(existingAnswers && Object.keys(existingAnswers).length > 0) && !isRejected;
  const [open, setOpen] = useState(!alreadySubmitted);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [embedSrc, setEmbedSrc] = useState<string | null>(null);
  const [submissionId, setSubmissionId] = useState<number | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const createSession = trpc.clientPortal.createMerchantInfoSession.useMutation({
    onSuccess: (data) => { setEmbedSrc(data.embedSrc); setSubmissionId(data.submissionId); },
    onError: (e: any) => setSessionError(e.message ?? "Could not load the form. Please try again."),
  });

  const saveMutation = trpc.clientPortal.saveMerchantInfo.useMutation({
    onSuccess: () => { toast.success("Merchant Information Form submitted!"); onSubmitted(); setOpen(false); },
    onError: (e: any) => toast.error(e.message ?? "Failed to save. Please try again."),
  });

  // Auto-create session on mount: card starts open for fresh forms but session was never triggered
  useEffect(() => {
    if (!alreadySubmitted && !embedSrc && !createSession.isPending) {
      createSession.mutate({ token });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !embedSrc && !alreadySubmitted && !isResubmitting && !createSession.isPending) {
      createSession.mutate({ token });
    }
  };

  const handleStartResubmit = () => {
    setIsResubmitting(true);
    setOpen(true);
    // Always force a new session — clear any cached embedSrc so DocuSeal
    // creates a fresh submission rather than reusing the completed one.
    setEmbedSrc(null);
    setSubmissionId(null);
    createSession.mutate({ token });
  };

  const handleComplete = (data: any) => {
    const docUrl = (data?.documents?.[0]?.url as string | undefined) ?? "";
    const sid = submissionId ?? (data?.submission?.id as number | undefined);
    saveMutation.mutate({
      token,
      answers: {
        completed_via: "docuseal",
        docuseal_submission_id: String(sid ?? ""),
        document_url: docUrl,
        completed_at: new Date().toISOString(),
      },
    });
    setIsResubmitting(false);
  };

  const headerIcon = isRejected
    ? <XCircle className="w-4 h-4 text-white" />
    : isApproved
      ? <CheckCircle className="w-4 h-4 text-white" />
      : isPendingReview
        ? <Clock className="w-4 h-4 text-white" />
        : <ClipboardList className="w-4 h-4 text-amber-600" />;
  const headerIconBg = isRejected ? "bg-red-500" : isApproved ? "bg-[#84ab4a]" : isPendingReview ? "bg-amber-400" : "bg-amber-100";
  const headerSub = isRejected
    ? "Action Required — form needs corrections"
    : isApproved
      ? "Approved"
      : isPendingReview
        ? "Pending Review"
        : "Required — complete the form below";

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <button className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" onClick={handleToggle}>
        <div className="flex items-center gap-3">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center ${headerIconBg}`}>
            {headerIcon}
          </div>
          <div className="text-left">
            <div className="text-sm font-bold text-gray-800">Step 3 — Merchant Information Form</div>
            <div className={`text-xs ${isRejected ? "text-red-500 font-semibold" : "text-gray-400"}`}>{headerSub}</div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-gray-100">
          {isRejected && !isResubmitting ? (
            <div className="px-6 py-6">
              <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm font-bold text-red-700">Form Needs Corrections</p>
                </div>
                <p className="text-xs font-semibold text-red-700 mb-1">Reason:</p>
                <p className="text-xs text-red-600">{merchantDoc?.reviewNote ?? "No reason provided — please contact your agent for details."}</p>
              </div>
              <button
                onClick={handleStartResubmit}
                className="w-full py-2.5 rounded-xl bg-[#84ab4a] text-white text-sm font-semibold hover:bg-[#6d9040] transition-colors"
              >
                Correct & Resubmit Form
              </button>
            </div>
          ) : isApproved ? (
            <div className="px-6 py-8 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-emerald-700">Merchant Information Form approved.</p>
            </div>
          ) : isPendingReview && !isResubmitting ? (
            <div className="px-6 py-8 text-center">
              <Clock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-amber-700">Pending Review</p>
              <p className="text-xs text-gray-400 mt-1">Your form has been received and is under review by our team.</p>
            </div>
          ) : createSession.isPending ? (
            <div className="px-6 py-12 text-center">
              <Loader2 className="w-8 h-8 text-[#84ab4a] animate-spin mx-auto" />
              <p className="text-sm text-gray-400 mt-3">Loading form…</p>
            </div>
          ) : sessionError ? (
            <div className="px-6 py-8 text-center">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-600 mb-3">{sessionError}</p>
              <button
                onClick={() => { setSessionError(null); createSession.mutate({ token }); }}
                className="text-xs text-[#84ab4a] underline hover:text-[#6f9139]"
              >
                Try again
              </button>
            </div>
          ) : embedSrc ? (
            <>
              {/* Multiple-owner / signer instruction */}
              <div className="px-6 pt-5">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-1">
                  <p className="text-xs font-bold text-amber-800 mb-1">Multiple Owners / Signers</p>
                  <p className="text-xs text-amber-700">
                    If the first signer owns 100% of the business, no additional signer information is required.
                    If ownership is less than 100%, please provide the additional owner/signer information in the form so that total ownership equals 100%.
                  </p>
                </div>
              </div>
              <DocusealForm
                src={embedSrc}
                withTitle={false}
                withDownloadButton={false}
                autoscrollFields={true}
                onComplete={handleComplete}
              />
              {/* Required documents note — shown alongside the form */}
              <div className="px-6 pb-6 pt-2">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-xs font-bold text-blue-800 mb-2">Your Share360 application also requires:</p>
                  <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Completed Processing Questionnaire (Step 1)</li>
                    <li>Current Processing Statements, 3 Months (Step 2)</li>
                    <li>Government ID, Front &amp; Back (Step 4 — after this form)</li>
                    <li>Current Bank Statement (Step 5 — after this form)</li>
                  </ol>
                  <p className="text-xs text-blue-500 mt-2 italic">Upload the remaining documents in Steps 4–5 below after submitting this form.</p>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ─── Main Client Portal ───────────────────────────────────────────────────────
export default function ClientPortal() {
  const [, params] = useLocation();
  const [resubmitDocId, setResubmitDocId] = useState<number | null>(null);
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token") ?? "";

  const { data, isLoading, error, refetch } = trpc.clientPortal.getProgress.useQuery(
    { token },
    { enabled: !!token, retry: false }
  );

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1628] to-[#0D2040] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-xl">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">No Access Link</h1>
          <p className="text-sm text-gray-500">This page requires a valid portal link. Please contact your agent to get your personal portal link.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1628] to-[#0D2040] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1628] to-[#0D2040] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-xl">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Link Expired or Invalid</h1>
          <p className="text-sm text-gray-500 mb-4">{error.message}</p>
          <p className="text-xs text-gray-400">Please contact your agent to get a new portal link.</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { lead, documents, session } = data;
  const verticalName = ((lead as any).verticalName as string | undefined) ?? "";
  const isShare360 = verticalName.startsWith("Share360");
  const isLegacy360 = verticalName === "Legacy360";
  const isNonprofit = verticalName.toLowerCase().includes("nonprofit");
  const questionnaireAnswers = ((lead as any).metadata as any)?.s360_questionnaire as Record<string, string> | undefined;
  const questionnaireSubmitted = !!(questionnaireAnswers && Object.keys(questionnaireAnswers).length > 0);
  const merchantInfoAnswers = ((lead as any).metadata as any)?.s360_merchant_info as Record<string, string> | undefined;
  const merchantInfoSubmitted = !!(merchantInfoAnswers && Object.keys(merchantInfoAnswers).length > 0);
  const hasGovId = documents.some(d => d.docType === "id_front" || d.docType === "id_back");
  const bankStatementCount = documents.filter(d => d.docType === "bank_statement").length;
  const hasEnoughBankStatements = bankStatementCount >= 3;
  const hasCurrentBankStatement = documents.some(d => d.docType === "current_bank_statement");
  // Nonprofit-specific required documents
  const has501c3 = documents.some(d => d.docType === "501c3");
  const hasVoidedCheck = documents.some(d => d.docType === "voided_check");
  const hasLogo = documents.some(d => d.docType === "logo");
  const nonprofitAgreementAnswers = ((lead as any).metadata as any)?.s360_nonprofit_agreement as Record<string, string> | undefined;
  const nonprofitAgreementSubmitted = !!(nonprofitAgreementAnswers && Object.keys(nonprofitAgreementAnswers).length > 0);
  const nonprofitAgreementDoc = documents.find((d: any) => d.docType === "nonprofit_agreement_form") ?? null;
  const allNonprofitUploadsComplete = hasGovId && has501c3 && hasVoidedCheck && hasLogo;
  const stageInfo = STAGE_LABELS[(lead as any).stage] ?? { label: (lead as any).stage, color: "#6B7280", step: 0 };
  const currentStep = stageInfo.step;
  const approvedCount = documents.filter(d => d.status === "approved").length;
  const pendingCount = documents.filter(d => d.status === "pending").length;
  const rejectedCount = documents.filter(d => d.status === "rejected").length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1628] to-[#0D2040]">
      {/* Header */}
      <div className="bg-[#0A1628]/80 border-b border-white/10 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#84ab4a] flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold text-white">BizConnect360</div>
              <div className="text-[10px] text-white/40">Client Portal</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-white/40">Logged in as</div>
            <div className="text-xs font-semibold text-white/70">{session.email}</div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Welcome card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-white mb-1">
                Welcome, {lead.firstName} {lead.lastName}
              </h1>
              <p className="text-sm text-white/50">Track your application progress and upload required documents below.</p>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="text-xs text-white/40 mb-1">Service</div>
              <div className="text-sm font-bold text-[#84ab4a]">{lead.verticalName ?? "BizConnect360"}</div>
            </div>
          </div>
          {lead.businessName && (
            <div className="mt-4 flex items-center gap-2 text-sm text-white/60">
              <Building2 className="w-4 h-4" /> {lead.businessName}
            </div>
          )}
        </div>

        {/* Share360: Questionnaire + Merchant Form + Required Docs Checklist */}
        {isShare360 ? (
          <>
            {/* Step 1: Processing Questionnaire */}
            <Share360Questionnaire
              token={token}
              existingAnswers={questionnaireAnswers}
              onSubmitted={() => refetch()}
              questions={isNonprofit ? S360_NONPROFIT_QUESTIONS : S360_BUSINESS_QUESTIONS}
              title={isNonprofit ? "Step 1 — Share360 Nonprofit Questionnaire" : "Step 1 — Share360 Processing Questionnaire"}
            />

            {/* Step 2 (Business only): Processing Statements — gated behind questionnaire.
                Ordered ahead of the Merchant Information Form so statements are collected
                immediately after the questionnaire, per the required business step order. */}
            {!isNonprofit && (
              isShare360Step2Unlocked({ questionnaireSubmitted }) ? (
                !hasEnoughBankStatements ? (
                  <UploadCard
                    key={`step2-statements-${bankStatementCount}`}
                    token={token}
                    onUploaded={() => refetch()}
                    allowedTypes={["bank_statement"]}
                  />
                ) : (
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#84ab4a] flex-shrink-0" />
                    <p className="text-xs text-white/60">Step 2 — Processing Statements complete ({bankStatementCount}/3 submitted).</p>
                  </div>
                )
              ) : (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-300 flex-shrink-0" />
                  <p className="text-xs text-white/60">Step 2 — Processing Statements unlocks after the Processing Questionnaire is submitted.</p>
                </div>
              )
            )}

            {/* Step 3 (Business only): Merchant Information Form (DocuSeal) — gated behind Processing Statements */}
            {!isNonprofit && (
              isShare360Step3Unlocked({ questionnaireSubmitted, hasEnoughBankStatements }) ? (
                <MerchantDocuSealCard
                  token={token}
                  existingAnswers={merchantInfoAnswers}
                  merchantDoc={documents.find((d: any) => d.docType === "merchant_info_form") ?? null}
                  onSubmitted={() => refetch()}
                />
              ) : questionnaireSubmitted ? (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-300 flex-shrink-0" />
                  <p className="text-xs text-white/60">Step 3 — Merchant Information Form unlocks after Processing Statements are complete.</p>
                </div>
              ) : null /* Step 2's own locked message above already explains the blocker */
            )}

            {/* Required Submissions Checklist */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wide">Required Submissions</h2>
              <div className="space-y-3">
                {(isNonprofit ? [
                  { label: "Share360 Nonprofit Questionnaire",   done: questionnaireSubmitted,    desc: "Complete the questionnaire above to continue",                optional: false },
                  { label: "Government ID",                      done: hasGovId,                  desc: "Upload a clear photo/scan of your government-issued ID",      optional: false },
                  { label: "501(c)(3) Determination Letter",     done: has501c3,                  desc: "Upload your IRS 501(c)(3) determination letter",              optional: false },
                  { label: "Voided Check",                       done: hasVoidedCheck,            desc: "Upload a voided check for your nonprofit bank account",        optional: false },
                  { label: "Organization Logo",                  done: hasLogo,                   desc: "Upload your nonprofit logo (PNG or JPG)",                     optional: false },
                  { label: "Marketing Materials",                done: documents.some(d => d.docType === "marketing_materials"), desc: "Upload any brochures or promotional materials (optional)", optional: true },
                  { label: "Nonprofit Agreement",                done: nonprofitAgreementSubmitted,   desc: "Sign the nonprofit agreement (unlocks after all required uploads)", optional: false },
                ] : [
                  { label: "Step 1 — Share360 Processing Questionnaire",   done: questionnaireSubmitted,   desc: "Complete the questionnaire above",                                                          optional: false },
                  { label: `Step 2 — Processing Statements (${bankStatementCount}/3 uploaded)`, done: hasEnoughBankStatements, desc: bankStatementCount > 0 ? `${bankStatementCount} of 3 submitted — upload ${3 - bankStatementCount} more` : "Upload 3 months of current processing statements", optional: false },
                  { label: "Step 3 — Merchant Information Form",           done: merchantInfoSubmitted,    desc: "Complete the Merchant Information Form above (unlocks after Step 2)",                        optional: false },
                  { label: "Step 4 — Government ID (Front & Back)",        done: hasGovId,                 desc: "Upload a clear photo/scan of your government-issued ID",                                     optional: false },
                  { label: "Step 5 — Current Bank Statement",              done: hasCurrentBankStatement,  desc: "Upload your most recent bank statement",                                                     optional: false },
                ]).map(item => (
                  <div key={item.label} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${item.done ? "bg-[#84ab4a]" : "bg-gray-200"}`}>
                      {item.done ? <CheckCircle className="w-4 h-4 text-white" /> : <Clock className="w-4 h-4 text-gray-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold truncate ${item.done ? "text-gray-800" : "text-gray-500"}`}>{item.label}</div>
                      {!item.done && <div className="text-xs text-gray-400 mt-0.5">{item.desc}</div>}
                    </div>
                    <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full flex-shrink-0 ${item.done ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : item.optional ? "bg-gray-50 text-gray-400 border border-gray-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                      {item.done ? "Complete" : item.optional ? "Optional" : "Required"}
                    </span>
                  </div>
                ))}
              </div>
              {isNonprofit
                ? questionnaireSubmitted && hasGovId && has501c3 && hasVoidedCheck && hasLogo && nonprofitAgreementSubmitted && (
                  <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm font-semibold text-emerald-700">All required items submitted — your application is complete!</span>
                  </div>
                )
                : isShare360BusinessComplete({ questionnaireSubmitted, hasEnoughBankStatements, merchantInfoSubmitted, hasGovId, hasCurrentBankStatement }) && (
                  <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm font-semibold text-emerald-700">All required items submitted — your application is complete!</span>
                  </div>
                )
              }
            </div>

            {/* Upload card — Steps 4–5 (Business): Government ID, then Current Bank Statement.
                Gated behind Steps 1–3 (Questionnaire, Processing Statements, Merchant Info Form).
                Nonprofit is unaffected by the business reorder and keeps its single combined gate. */}
            {(isNonprofit ? questionnaireSubmitted : isShare360Steps4And5Unlocked({ questionnaireSubmitted, hasEnoughBankStatements, merchantInfoSubmitted })) ? (
              <UploadCard
                key={isNonprofit
                  ? "nonprofit-all"
                  : getShare360Step4Or5DocType({ hasGovId }) === "gov_id"
                    ? "step4-govid"
                    : "step5-bankstmt"
                }
                token={token}
                onUploaded={() => refetch()}
                allowedTypes={isNonprofit
                  ? ["id_front", "id_back", "501c3", "voided_check", "logo", "marketing_materials"]
                  : getShare360Step4Or5DocType({ hasGovId }) === "gov_id"
                    ? ["id_front", "id_back"]
                    : ["current_bank_statement"]
                }
              />
            ) : (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                <AlertCircle className="w-8 h-8 text-amber-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-white/80 mb-1">Document uploads are locked</p>
                <p className="text-xs text-white/50">
                  {!questionnaireSubmitted
                    ? isNonprofit
                      ? "Please complete Step 1 (Share360 Nonprofit Questionnaire) to unlock document uploads."
                      : "Please complete Step 1 (Share360 Processing Questionnaire) to unlock document uploads."
                    : !isNonprofit && !hasEnoughBankStatements
                      ? "Please complete Step 2 (Processing Statements) to unlock document uploads."
                      : !isNonprofit && !merchantInfoSubmitted
                        ? "Please complete Step 3 (Merchant Information Form) to unlock document uploads."
                        : "Please complete the steps above to unlock document uploads."}
                </p>
              </div>
            )}

            {/* Step 2 (Nonprofit only): Nonprofit Agreement — gated behind all required uploads */}
            {isNonprofit && (
              allNonprofitUploadsComplete ? (
                <NonprofitAgreementCard
                  token={token}
                  existingAnswers={nonprofitAgreementAnswers}
                  agreementDoc={nonprofitAgreementDoc}
                  onSubmitted={() => refetch()}
                />
              ) : questionnaireSubmitted ? (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-300 flex-shrink-0" />
                  <p className="text-xs text-white/60">Step 2 — Nonprofit Agreement unlocks after uploading all required documents (Government ID, 501(c)(3) Letter, Voided Check, and Organization Logo).</p>
                </div>
              ) : null
            )}
          </>
        ) : isLegacy360 ? (
          /* Legacy360 Living Trust Portal */
          <Legacy360DocumentSection
            token={token}
            documents={documents}
            onUploaded={() => refetch()}
          />
        ) : (
          <>
            {/* Progress pipeline */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-sm font-bold text-gray-700 mb-5 uppercase tracking-wide">Application Progress</h2>
              <div className="flex items-center gap-0">
                {PIPELINE_STEPS.map((stage, idx) => {
                  const info = STAGE_LABELS[stage];
                  const isCompleted = currentStep > info.step;
                  const isCurrent = currentStep === info.step;
                  const isLast = idx === PIPELINE_STEPS.length - 1;
                  return (
                    <div key={stage} className="flex items-center flex-1">
                      <div className="flex flex-col items-center gap-1.5 flex-1">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                          isCompleted ? "bg-[#84ab4a] border-[#84ab4a] text-white" :
                          isCurrent ? "bg-white border-[#84ab4a] text-[#84ab4a]" :
                          "bg-gray-100 border-gray-200 text-gray-400"
                        }`}>
                          {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : info.step}
                        </div>
                        <div className={`text-[9px] font-semibold text-center leading-tight max-w-[60px] ${isCurrent ? "text-[#84ab4a]" : isCompleted ? "text-gray-600" : "text-gray-400"}`}>
                          {info.label}
                        </div>
                      </div>
                      {!isLast && (
                        <div className={`h-0.5 flex-1 -mt-5 ${isCompleted ? "bg-[#84ab4a]" : "bg-gray-200"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: `${stageInfo.color}15` }}>
                <div className="w-2 h-2 rounded-full" style={{ background: stageInfo.color }} />
                <span className="text-sm font-semibold" style={{ color: stageInfo.color }}>Current Status: {stageInfo.label}</span>
              </div>
            </div>

            {/* Document summary */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Submitted", count: approvedCount + pendingCount, color: "#10B981", icon: CheckCircle },
                { label: "Under Review", count: pendingCount, color: "#F59E0B", icon: Clock },
                { label: "Action Needed", count: rejectedCount, color: "#EF4444", icon: XCircle },
              ].map(({ label, count, color, icon: Icon }) => (
                <div key={label} className="bg-white rounded-xl p-4 shadow-sm text-center border border-gray-100">
                  <Icon className="w-5 h-5 mx-auto mb-1" style={{ color }} />
                  <div className="text-2xl font-bold" style={{ color }}>{count}</div>
                  <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide">{label}</div>
                </div>
              ))}
            </div>

            {/* Upload card */}
            <UploadCard token={token} onUploaded={() => refetch()} />
          </>
        )}

        {/* Documents list — hidden for Legacy360 since uploads are shown inline per document type */}
        {documents.length > 0 && !isLegacy360 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Uploaded Documents</h3>
              <button onClick={() => refetch()} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>

            {/* Action required banner */}
            {rejectedCount > 0 && (
              <div className="flex items-start gap-3 p-4 mb-4 rounded-xl bg-red-50 border border-red-200">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-700">
                    Action Required — {rejectedCount} document{rejectedCount > 1 ? "s" : ""} need{rejectedCount === 1 ? "s" : ""} resubmission
                  </p>
                  <p className="text-xs text-red-600 mt-0.5">
                    Review the rejection reason on each document below and upload a corrected file.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {documents.map(doc => (
                <div key={doc.id} className={`rounded-xl border ${doc.status === "rejected" ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-100"}`}>
                  {/* Document row */}
                  <div className="flex items-center gap-3 p-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${doc.status === "rejected" ? "bg-red-100" : "bg-[#84ab4a]/10"}`}>
                      {doc.status === "rejected"
                        ? <XCircle className="w-4 h-4 text-red-500" />
                        : <FileText className="w-4 h-4 text-[#84ab4a]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">{doc.docLabel ?? doc.docType}</div>
                      <div className="text-xs text-gray-400 truncate">{doc.fileName}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <DocStatusBadge status={doc.status} />
                      <a href={`/api/files/${doc.fileKey}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>

                  {/* Rejection callout + inline resubmit */}
                  {doc.status === "rejected" && (
                    <div className="px-3 pb-3 space-y-2">
                      <div className="rounded-lg bg-red-100 border border-red-200 px-3 py-2">
                        <p className="text-xs font-semibold text-red-700 mb-0.5">Reason for rejection</p>
                        <p className="text-xs text-red-600">
                          {doc.reviewNote ?? "No reason provided — please contact your agent for details."}
                        </p>
                      </div>
                      <button
                        onClick={() => setResubmitDocId(resubmitDocId === doc.id ? null : doc.id)}
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-red-300 bg-white text-xs font-semibold text-red-700 hover:bg-red-50 transition-colors"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        {resubmitDocId === doc.id ? "Cancel" : "Resubmit Document"}
                      </button>
                      {resubmitDocId === doc.id && (
                        <UploadCard
                          token={token}
                          onUploaded={() => { setResubmitDocId(null); refetch(); }}
                          allowedTypes={[doc.docType]}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-white/20 pb-4">
          Portal link expires {new Date(session.expiresAt).toLocaleDateString()} · BizConnect360 Secure Portal
        </div>
      </div>
    </div>
  );
}
